import { callingCodes, CallingCodeCountry } from './calling-codes';

export const NATIONAL_PHONE_MIN = 7;
export const NATIONAL_PHONE_MAX = 15;

const dialCodes = Array.from(new Set(callingCodes.map(country => country.dialCode))).sort(
  (left, right) => right.length - left.length,
);

const nationalGroups: Record<number, number[]> = {
  7: [3, 4],
  8: [4, 4],
  9: [3, 3, 3],
  10: [3, 3, 4],
  11: [3, 4, 4],
  12: [4, 4, 4],
  13: [3, 3, 3, 4],
  14: [3, 3, 4, 4],
  15: [3, 4, 4, 4],
};

const legacyValues = new Set<string>();

export interface ParsedPhone {
  callingCode: string | null;
  nationalNumber: string;
}

export function parsePhone(value: string | null | undefined): ParsedPhone {
  const raw = (value ?? '').trim();

  if (!raw) {
    return { callingCode: null, nationalNumber: '' };
  }

  if (!raw.startsWith('+')) {
    return parseDigitsWithoutPlus(raw);
  }

  const digits = raw.slice(1);

  if (!digits || !/^\d+$/.test(digits)) {
    return { callingCode: null, nationalNumber: raw };
  }

  const callingCode = dialCodes.find(code => digits.startsWith(code));

  if (!callingCode) {
    return { callingCode: null, nationalNumber: raw };
  }

  return {
    callingCode,
    nationalNumber: digits.slice(callingCode.length),
  };
}

export function hasCallingCode(value: string): boolean {
  const raw = value.trim();

  if (!raw.startsWith('+')) {
    return false;
  }

  const digits = raw.slice(1);

  if (!/^\d+$/.test(digits)) {
    return false;
  }

  const callingCode = dialCodes.find(code => digits.startsWith(code));

  if (!callingCode) {
    return false;
  }

  const nationalNumber = digits.slice(callingCode.length);

  return nationalNumber.length >= NATIONAL_PHONE_MIN && nationalNumber.length <= NATIONAL_PHONE_MAX;
}

export function rememberLegacyPhone(value: string | null | undefined) {
  const raw = (value ?? '').trim();

  if (!raw || raw === 'null' || raw.startsWith('+')) {
    return;
  }

  legacyValues.add(raw);
}

export function isStoredPhoneValue(value: string | null | undefined, required = false): boolean {
  const raw = (value ?? '').trim();

  if (!raw || raw === 'null') {
    return !required;
  }

  if (hasCallingCode(raw)) {
    return true;
  }

  return legacyValues.has(raw);
}

export function splitPhone(value: string | null | undefined): { dialCode: string; nationalNumber: string } {
  const raw = (value ?? '').trim();
  const parsed = parsePhone(raw);

  if (parsed.callingCode) {
    return {
      dialCode: parsed.callingCode,
      nationalNumber: parsed.nationalNumber.replace(/\D/g, '').slice(0, NATIONAL_PHONE_MAX),
    };
  }

  return {
    dialCode: '',
    nationalNumber: raw.replace(/\D/g, '').slice(0, NATIONAL_PHONE_MAX),
  };
}

export function composeStoredPhone(dialCode: string, nationalNumber: string, legacyOriginal: string): string {
  if (!nationalNumber) {
    return '';
  }

  if (dialCode) {
    return `+${dialCode}${nationalNumber}`;
  }

  const original = legacyOriginal.trim();

  if (original && !original.startsWith('+')) {
    const originalDigits = original.replace(/\D/g, '');

    if (nationalNumber === originalDigits || nationalNumber === original) {
      return original;
    }
  }

  return nationalNumber;
}

export function formatPhoneForDisplay(value: string | number | null | undefined): string {
  if (value == null) {
    return '';
  }

  const raw = String(value).trim();

  if (!raw) {
    return '';
  }

  const parsed = parsePhone(raw);

  if (!parsed.callingCode) {
    if (/^\d{10}$/.test(parsed.nationalNumber)) {
      return formatNanpLocal(parsed.nationalNumber);
    }

    return raw;
  }

  if (!parsed.nationalNumber || !/^\d+$/.test(parsed.nationalNumber)) {
    return raw;
  }

  if (parsed.callingCode === '1' && parsed.nationalNumber.length === 10) {
    return `1 ${formatNanpLocal(parsed.nationalNumber)}`;
  }

  return `+(${parsed.callingCode}) ${groupNational(parsed.nationalNumber)}`;
}

export function countryName(country: CallingCodeCountry, language: string): string {
  return language.toLowerCase().startsWith('es') ? country.nameEs : country.nameEn;
}

export function countryFlagEmoji(iso2: string): string {
  const code = (iso2 || '').toUpperCase();

  if (!/^[A-Z]{2}$/.test(code)) {
    return '';
  }

  const points: number[] = [];
  for (let i = 0; i < code.length; i++) {
    points.push(127397 + code.charCodeAt(i));
  }

  return String.fromCodePoint.apply(null, points);
}

export function countryOptionLabel(country: CallingCodeCountry, language: string): string {
  const flag = countryFlagEmoji(country.iso2);
  return [flag, countryName(country, language), `+${country.dialCode}`].filter(Boolean).join(' ');
}

export function matchesCallingCodeQuery(country: CallingCodeCountry, input: string): boolean {
  const query = input.trim().toLowerCase();

  if (!query) {
    return true;
  }

  if (country.nameEn.toLowerCase().includes(query) || country.nameEs.toLowerCase().includes(query)) {
    return true;
  }

  const digits = query.replace(/^\+/, '');

  if (/^\d+$/.test(digits)) {
    return country.dialCode.startsWith(digits);
  }

  return false;
}

function parseDigitsWithoutPlus(raw: string): ParsedPhone {
  if (!/^\d+$/.test(raw) || raw.length === 10) {
    return { callingCode: null, nationalNumber: raw };
  }

  const callingCode = dialCodes.find(code => {
    if (!raw.startsWith(code) || code.length >= raw.length) {
      return false;
    }

    const nationalLength = raw.length - code.length;

    return nationalLength >= NATIONAL_PHONE_MIN && nationalLength <= NATIONAL_PHONE_MAX;
  });

  if (!callingCode) {
    return { callingCode: null, nationalNumber: raw };
  }

  return {
    callingCode,
    nationalNumber: raw.slice(callingCode.length),
  };
}

function formatNanpLocal(nationalNumber: string): string {
  return `(${nationalNumber.slice(0, 3)}) ${nationalNumber.slice(3, 6)} ${nationalNumber.slice(6)}`;
}

function groupNational(nationalNumber: string): string {
  const sizes = nationalGroups[nationalNumber.length];

  if (!sizes) {
    return nationalNumber;
  }

  const parts: string[] = [];
  let offset = 0;

  for (const size of sizes) {
    parts.push(nationalNumber.slice(offset, offset + size));
    offset += size;
  }

  return parts.join(' ');
}
